import { jwtDecode } from "jwt-decode";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { API_ORIGIN } from "../api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const getUserIdFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return decoded.sub || null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [authToken, setAuthToken] = useState(localStorage.getItem("authToken"));
  const [user, setUser] = useState(null);

  const logout = useCallback(() => {
    setAuthToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/";
  }, []);

  const fetchCurrentUser = useCallback(async (token) => {
    if (!token) {
      setUser(null);
      return null;
    }
    const userId = getUserIdFromToken(token);
    if (!userId) {
      setUser(null);
      return null;
    }
    try {
      const response = await fetch(`${API_ORIGIN}/v1/users/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        setUser(null);
        return null;
      }
      const data = await response.json();
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  const login = useCallback(
    (token, userFromLogin = null) => {
      setAuthToken(token);
      localStorage.setItem("authToken", token);
      if (userFromLogin) {
        setUser(userFromLogin);
      } else {
        fetchCurrentUser(token);
      }
    },
    [fetchCurrentUser]
  );

  const refreshToken = useCallback(async () => {
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (!storedRefreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_ORIGIN}/v1/auth/refresh-tokens`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        const accessToken = data.access?.token || data.accessToken;
        const nextRefreshToken = data.refresh?.token || data.refreshToken;
        if (!accessToken) {
          return null;
        }
        localStorage.setItem("authToken", accessToken);
        if (nextRefreshToken) {
          localStorage.setItem("refreshToken", nextRefreshToken);
        }
        login(accessToken);
        return accessToken;
      }
      return null;
    } catch {
      return null;
    }
  }, [login]);

  useEffect(() => {
    const checkTokenExpiry = async () => {
      if (authToken && isTokenExpired(authToken)) {
        const newToken = await refreshToken();
        if (!newToken) {
          logout();
        }
      }
    };

    checkTokenExpiry();

    const interval = setInterval(checkTokenExpiry, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [authToken, logout, refreshToken]);

  // Load user profile on mount / when token changes (session restore)
  useEffect(() => {
    if (!authToken) {
      setUser(null);
      return;
    }
    if (isTokenExpired(authToken)) {
      return;
    }
    fetchCurrentUser(authToken);
  }, [authToken, fetchCurrentUser]);

  const isTokenExpired = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp < Date.now() / 1000;
    } catch {
      return true;
    }
  };

  const isAdmin = user?.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        authToken,
        user,
        isAdmin,
        isAuthenticated: !!authToken,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
